import { useMemo, useState } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { useValidationSchema } from "src/ui/utils/useValidationSchema";

import { PrimaryButton, PrimaryOutlinedButton, SecondaryButton } from "@shared/CustomButton";
import { SectionField } from "@shared/SectionPanel/SectionField";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { getErrorMessage } from "@shared/toasts/useQueryErrorToast";

import { useUpdateWorkspaceMutation } from "../../api";
import { displayOverviewValue, EditableField, FormColumn } from "../WorkspaceFormColumn/FormColumn";
import {
    WorkspaceDetailsFormValues,
    workspaceDetailsValidationSchema,
} from "../WorkspaceFormColumn/WorkspaceFormColumn.validation";

type WorkspaceDetailsSectionProps = {
    workspace: API.Workspace;
    canEdit: boolean;
};

export function WorkspaceDetailsSection(props: WorkspaceDetailsSectionProps) {
    const { t } = useTranslation(["common", "pages"]);
    const [updateWorkspace] = useUpdateWorkspaceMutation();
    const [editing, setEditing] = useState(false);
    const validate = useValidationSchema(workspaceDetailsValidationSchema);

    const { name } = props.workspace;
    const initialValues = useMemo<WorkspaceDetailsFormValues>(() => ({ name }), [name]);

    const workspaceFields: EditableField<keyof WorkspaceDetailsFormValues>[] = [
        { name: "name", label: t("pages:workspacePage.input.name.caption"), autoFocus: true },
    ];

    const onSubmit = async (values: WorkspaceDetailsFormValues) => {
        try {
            await updateWorkspace({
                ...props.workspace,
                name: values.name.trim(),
            }).unwrap();
            showSuccessToast(t("pages:workspacePage.toast.workspaceUpdateSuccess"));
            setEditing(false);
        } catch (error) {
            showErrorToast(getErrorMessage(error));
        }
    };

    return (
        <Form<WorkspaceDetailsFormValues>
            initialValues={initialValues}
            onSubmit={onSubmit}
            validate={validate}
            render={({ handleSubmit, form, invalid, pristine, submitting }) => (
                <form onSubmit={handleSubmit} noValidate>
                    <SectionPanel
                        title={t("pages:workspacePage.section.title.workspaceDetails")}
                        actions={
                            editing ? (
                                <>
                                    <SecondaryButton
                                        type="button"
                                        onClick={() => {
                                            form.reset();
                                            setEditing(false);
                                        }}
                                        disabled={submitting}
                                    >
                                        {t("common:button.cancel")}
                                    </SecondaryButton>
                                    <PrimaryButton
                                        type="submit"
                                        disabled={invalid || pristine || submitting}
                                        loading={submitting}
                                    >
                                        {t("common:button.save")}
                                    </PrimaryButton>
                                </>
                            ) : (
                                <PrimaryOutlinedButton
                                    onClick={() => setEditing(true)}
                                    disabled={!props.canEdit}
                                    startIcon={<i className="fa-solid fa-pencil" />}
                                    tooltipProps={{
                                        title: t("pages:workspacePage.tooltip.workspaceRoleChangeNotAllowed"),
                                        hide: props.canEdit,
                                        placement: "top",
                                    }}
                                >
                                    {t("common:button.edit")}
                                </PrimaryOutlinedButton>
                            )
                        }
                    >
                        <FormColumn fields={workspaceFields} values={initialValues} editing={editing}>
                            <SectionField
                                label={t("pages:workspacePage.input.guid.caption")}
                                value={displayOverviewValue(props.workspace.guid)}
                            />
                        </FormColumn>
                    </SectionPanel>
                </form>
            )}
        />
    );
}
